import {PushpinFilled, PushpinOutlined} from '@ant-design/icons';
import {Badge, Button, Tooltip} from 'antd';
import Column from 'antd/es/table/Column';
import ColumnGroup from 'antd/es/table/ColumnGroup';
import {SortOrder} from 'antd/es/table/interface';
import cn from 'classnames';
import React from 'react';

import {TableColumnFilter} from 'components/@common/widgets/custom-table/table-column-filter/table-column-filter';
import {
    TGetTableColumnFilterDataFunction,
    TGetTableColumnFilterInfoFunction,
    TSetTableColumnFilterDataFunction,
} from 'components/@common/widgets/custom-table/table-column-filter/use-table-column-filter';
import {TableColumnMenu} from 'components/@common/widgets/custom-table/table-column-menu';
import {TGetSortDataFunction, TSetSortDataFunction} from 'components/@common/widgets/custom-table/table-column-menu/hooks/use-table-column-sort';
import {
    ITableHeaderCell,
    TTableHeaderStructure,
} from 'components/report-configuration/tabs/report-table-settings/modals/excel-header-structure-loader-modal/excel-header-structure-loader-modal.types';
import {ReportColumnContent} from 'components/table-report/components/table-report-column-content';
import {TLoadTableReportPageDataFunction} from 'components/table-report/hooks/use-control';
import {TableRowAttachements} from 'components/table-report/table-row-attachments/table-row-attachments';
import {
    IApplyFormatSettingsToRowArgs,
    IFormatSettingsApplicationResult,
} from 'components/table-report/utils/table-report-utils';
import {ReactComponent as MoreIcon} from 'shared/assets/more.svg';
import {TStateSetter} from 'shared/types/generics';
import {getPopupContainer} from 'shared/utils';
import {store} from 'store/config';
import {TAppState} from 'store/config/types';
import {IReportConfigurationAttribute} from 'store/slices/report-configuration-slice';
import {
    IReportConfigurationRowFormat,
} from 'store/slices/report-configuration-slice/report-configuration-dto';
import {TABLE_REPORT_URL_FIELD_NAME} from 'store/slices/table-report-slice/table-report-slice-constants';
import {selectLinkDtoToColumnMapping, selectTableReportFiltersByFieldType} from 'store/slices/table-report-slice/table-report-slice-selectors';
import {
    TTableReportColumn,
    ITableReportDrillDown,
    TTableReportExtendedConfiguration,
} from 'store/slices/table-report-slice/table-report-slice-types';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {TPushWithBreadcrumbsFunction} from '../hooks/use-breadcrumbs-controller';
import {TableRowDrillUp} from '../table-row-drillup';
import {
    NonbreakingSpaces,
    extractIndexesFromKey,
    getColumnHeaderWidthStyles,
    getColumnWidthStyles,
    isColumnTypeOfString,
    isCustomColumn,
    getMultiRowHeaderCellStyle,
    getTreeMaxColumnIndex,
    addLinkColumnsToHeaderStructure,
} from './table-report-columns-utils';
import './table-report-columns.less';

export interface ITableReportColumnProps {
    docId: string | null;
    templateCode: string;
    enabledRowAttachments: boolean | undefined;
    enabledRowComments: boolean | undefined;
    enabledRowRequests: boolean | undefined;
    enabledSelectAllRowsBtn: boolean | undefined;
    enabledDrillUpBtn: boolean | undefined;

    createTableColumnFilterDataGetter: (columnKey: string) => TGetTableColumnFilterDataFunction;
    createTableColumnFilterDataSetter: (columnKey: string) => TSetTableColumnFilterDataFunction;
    getTableColumnFilterInfo: TGetTableColumnFilterInfoFunction;

    createSortDataGetter: (columnKey: string) => TGetSortDataFunction;
    createSortDataSetter: (columnKey: string) => TSetSortDataFunction;

    headerFontSize: string;
    tableFontSize: string;
    hideColumn: (columnKey: string) => void;
    applyFormatSettingsToRow: (
        {dataIndex, record, rowFormats}: IApplyFormatSettingsToRowArgs,
    ) => IFormatSettingsApplicationResult;
    drillDowns: ITableReportDrillDown[] | undefined;
    pushWithBreadcrumbs: TPushWithBreadcrumbsFunction;
    setIsDDModalVisible: TStateSetter<boolean>;
    setDDModalEntries: TStateSetter<ITableReportDrillDown[] | undefined>;
    rowFormats: IReportConfigurationRowFormat[];
    loadTableReportData: TLoadTableReportPageDataFunction;
    tableReportAttributes?: Record<string, string>;
    reportGroups?: TTableReportExtendedConfiguration['reportGroups'];
    handlePinButtonClick?: () => void;
    isTableHeaderPinned?: boolean;
    enabledPinHeaderButton?: boolean;
    enabledMenu?: boolean;
    hasRolePermission: (func: EUserRoleFunction) => boolean;
    tableReportAttributesForFilters?: IReportConfigurationAttribute[];
}

interface ITableReportHeaderProps {
    headerStructure: TTableHeaderStructure | undefined;
    columns: TTableReportColumn[] | undefined;
    columnProps: ITableReportColumnProps;
    disableActions?: boolean;
}

type TSingleRowHeaderProps = Pick<ITableReportHeaderProps, 'columns' | 'columnProps' | 'disableActions'>;

type TMultiRowHeaderArgs = {
    node: ITableHeaderCell;
    parentDataIndex?: string;
    firstRowLastColumnIndex: number | undefined;
    maxColumnIndex: number | undefined;
    disableActions?: boolean;
} & Pick<ITableReportHeaderProps, 'columns' | 'columnProps'>;

// Проверка на отображение колонки в таблице
const isColumnVisible = (node: ITableHeaderCell, columns: TTableReportColumn[] | undefined): boolean => {
    if (!columns) return false;

    // Если у узла есть атрибут — ищем колонку с таким dataIndex
    if (node.attribute) {
        const column = columns.find(col => col.dataIndex === node.attribute);
        return column ? !column.hidden : false;
    }

    // Номер будет показан, если его генерирует видимый родитель
    if (/^\d+/.test(node.key?.toString()) && !node.attribute) {
        return true;
    }

    // Для групповых узлов: хотя бы одна дочерняя колонка должна быть видимой
    return node.children?.some(child => isColumnVisible(child, columns)) || false;
};

export const convertHeaderRowIntoColumnGroup = ({
    node,
    columns,
    columnProps,
    parentDataIndex,
    firstRowLastColumnIndex,
    maxColumnIndex,
    disableActions,
}: TMultiRowHeaderArgs) => {
    const rowSpan = Number(node?.cellHeight) > 1 ? Number(node?.cellHeight) : undefined;
    const cellColumn = columns?.find(c => c.dataIndex === (node?.attribute ?? parentDataIndex));

    const dataIndex = (
        node?.attribute ? cellColumn?.dataIndex : parentDataIndex
    ) ?? undefined;
    const title = node?.attribute
        ? cellColumn?.title
        : node?.title;

    // todo: find a better way to extract indexes
    const {row: cellRowIndex} = extractIndexesFromKey(node.key) ?? {};
    const cellColumnIndex = cellColumn?.key != null ? cellColumn?.key : undefined;

    const {
        templateCode,

        createSortDataGetter,
        createSortDataSetter,
        createTableColumnFilterDataGetter,
        createTableColumnFilterDataSetter,
        getTableColumnFilterInfo,

        headerFontSize,
        tableFontSize,
        hideColumn,
        applyFormatSettingsToRow,
        drillDowns,
        pushWithBreadcrumbs,
        setIsDDModalVisible,
        setDDModalEntries,
        rowFormats,
        tableReportAttributes,
        reportGroups,

        hasRolePermission,
        tableReportAttributesForFilters,
        docId,
    } = columnProps;

    const cellStyle = getMultiRowHeaderCellStyle({
        cellRowIndex,
        cellColumnIndex,
        firstRowLastColumnIndex,
        maxColumnIndex,
    });

    const linkDtoToColumnMapping = selectLinkDtoToColumnMapping(store.getState() as TAppState);

    const commonRenderProps = {
        className: cellStyle.borderClassname,
        key: node?.key,
        dataIndex,
        rowSpan: Number(node?.cellHeight) === -1 ? 0 : rowSpan,
        title: (() => {
            const styles = getColumnHeaderWidthStyles(cellColumn);

            return (
                <div
                    className={cn('table-report__table__column-title')}
                    style={{
                        fontSize: headerFontSize,
                    }}
                >
                    <div style={{...styles, flexGrow: 1}}>
                        <NonbreakingSpaces>
                            {title}
                        </NonbreakingSpaces>
                    </div>
                    {(() => {
                        if (!dataIndex) return undefined;

                        const filterInfo = getTableColumnFilterInfo(dataIndex);

                        return (!parentDataIndex && !disableActions) && (
                            <Badge
                                count={filterInfo.rulesCount}
                                size="small"
                            >
                                <TableColumnFilter
                                    templateCode={templateCode}
                                    columnName={dataIndex}
                                    getTableColumnFilterData={createTableColumnFilterDataGetter(dataIndex)}
                                    setTableColumnFilterData={createTableColumnFilterDataSetter(dataIndex)}
                                    isApplied={filterInfo.isApplied}
                                    hasRolePermission={hasRolePermission}
                                    selectColumnFilterEntriesByFieldType={selectTableReportFiltersByFieldType}
                                    tableAttributes={tableReportAttributesForFilters}
                                />
                            </Badge>
                        );
                    })()}
                </div>
            );
        })(),
        onHeaderCell: () => ({style: cellStyle?.style}),
        sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
    };

    const dataIndexDependentRenderProps = {
        sorter: (dataIndex && !parentDataIndex
            ? (() => {
                const columnSortInfo = createSortDataGetter(dataIndex)();
                if (columnSortInfo.order && columnSortInfo.multiple) {
                    return {
                        // compare: createDefaultSorter(column.dataIndex),
                        compare: () => 0,
                        multiple: columnSortInfo.multiple,
                    };
                }
                return undefined;
            })()
            : undefined
        ),
        sortOrder: (dataIndex && !parentDataIndex
            ? (() => {
                const columnSortInfo = createSortDataGetter(dataIndex)();
                return columnSortInfo.order ? columnSortInfo.order : null;
            })()
            : undefined
        ),
        filterIcon: (dataIndex && !parentDataIndex && !isCustomColumn(dataIndex)
            ? (
                <span className="anticon anticon-filter">
                    <MoreIcon
                        height={14}
                        width={14}
                        fill="currentColor"
                    />
                </span>
            )
            : undefined
        ),
        filterDropdown: (dataIndex && !parentDataIndex && !isCustomColumn(dataIndex) && !disableActions
            ? (() => (
                <TableColumnMenu
                    setSortData={createSortDataSetter(dataIndex)}
                    getSortData={createSortDataGetter(dataIndex)}
                    handleHideColumnClick={() => hideColumn(dataIndex)}
                    hideOptions={
                        dataIndex.includes(TABLE_REPORT_URL_FIELD_NAME)
                         && !linkDtoToColumnMapping?.[dataIndex]
                    }
                />
            ))
            : undefined
        ),
        render: dataIndex
            ? ((val: any, record: any, rowIndex: number) => {
                const formatSettingsApplicationResult = applyFormatSettingsToRow({
                    dataIndex,
                    record,
                    rowFormats,
                    reportGroups,
                });

                return {
                    props: {
                        className: cn({
                            'table-report-string-column': isColumnTypeOfString(
                                tableReportAttributes,
                                cellColumn,
                            ),
                        }),
                        style: {
                            background: formatSettingsApplicationResult.background,
                            ...getColumnWidthStyles(
                                tableReportAttributes,
                                cellColumn,
                            ),
                        },
                    },
                    children: cellColumnIndex !== undefined && (
                        <div
                            className="table-report__column"
                            style={{fontSize: tableFontSize}}
                        >
                            <ReportColumnContent
                                {...{
                                    record,
                                    value: val,
                                    columnIndex: cellColumnIndex,
                                    rowIndex,
                                    formatSettingsApplicationResult,
                                    column: cellColumn,
                                    tableFontSize,
                                    pushWithBreadcrumbs,
                                    drillDowns,
                                    setDDModalEntries,
                                    setIsDDModalVisible,
                                    docId,
                                }}
                            />
                        </div>
                    ),
                };
            }) : undefined,
    };

    if (node?.children?.length) {
        return (
            <ColumnGroup
                {...commonRenderProps}
                {...dataIndexDependentRenderProps}
                render={undefined}
            >
                {node?.children?.map(child => (
                    /**
                     * * Определяет, следует ли генерировать дочерний узел заголовка как колонку или группу колонок.
                     *
                     * @param {ITableHeaderCell} child - Дочерний узел заголовка для обработки.
                     * @param {TTableReportColumn[] | undefined} columns - Список колонок таблицы,
                     *   используемый для проверки видимости узла через `isColumnVisible`.
                     * @param {string | undefined} dataIndex - Индекс данных родительского узла.
                     * @param {number | undefined} firstRowLastColumnIndex - Индекс последней колонки в первой строке.
                     * @param {number | undefined} maxColumnIndex - Максимальный индекс колонки.
                     * @param {boolean} disableActions - Флаг, указывающий, отключены ли действия.
                     *
                     * Логика:
                     * 1. Проверяет видимость узла `child` с помощью `isColumnVisible(child, columns)`.
                     * 2. Если узел виден — рекурсивно вызывает `convertHeaderRowIntoColumnGroup` для него,
                     *    передавая текущие параметры.
                     * 3. Если узел не виден — возвращает пустой фрагмент `<></>`, чтобы не генерировать его.
                     *
                     * @returns {JSX.Element | null} Возвращает элемент группы колонок или <></>,
                     * если условия не выполняются.
                     */
                    isColumnVisible(child, columns) ?
                        convertHeaderRowIntoColumnGroup({
                            node: child,
                            columns,
                            columnProps,
                            parentDataIndex: dataIndex,
                            firstRowLastColumnIndex,
                            maxColumnIndex,
                            disableActions,
                        }) : <></>))}
            </ColumnGroup>
        );
    }
    return (
        <Column
            {...commonRenderProps}
            {...dataIndexDependentRenderProps}
        />
    );
};

const renderMultiRowHeader = ({
    headerStructure, columns, columnProps, disableActions = false,
}: ITableReportHeaderProps) => {
    if (!headerStructure) {
        return undefined;
    }

    const {column: firstRowLastColumnIndex} = (
        extractIndexesFromKey(headerStructure[headerStructure.length - 1]?.key) ?? {}
    );
    const maxColumnIndex = getTreeMaxColumnIndex(headerStructure);

    const headerStructureWithLinks = addLinkColumnsToHeaderStructure(headerStructure, columns);

    return headerStructureWithLinks?.map(node => {
        if (isColumnVisible(node, columns)) {
            return convertHeaderRowIntoColumnGroup({
                node,
                columns,
                columnProps,
                firstRowLastColumnIndex,
                maxColumnIndex,
                disableActions,
            });
        }

        return null;
    });
};

const renderSingleRowHeader = ({columns, columnProps, disableActions = false}: TSingleRowHeaderProps) => {
    const {
        templateCode,

        createTableColumnFilterDataGetter,
        createTableColumnFilterDataSetter,
        getTableColumnFilterInfo,

        createSortDataGetter,
        createSortDataSetter,

        headerFontSize,
        tableFontSize,
        hideColumn,
        applyFormatSettingsToRow,
        drillDowns,
        pushWithBreadcrumbs,
        setIsDDModalVisible,
        setDDModalEntries,
        rowFormats,
        tableReportAttributes,
        reportGroups,
        enabledMenu = false,

        hasRolePermission,
        tableReportAttributesForFilters,
        docId,
    } = columnProps;

    const linkDtoToColumnMapping = selectLinkDtoToColumnMapping(store.getState() as TAppState);

    return (
        <>
            {columns?.map((column, columnIndex) => (
                <>
                    <Column
                        title={(() => {
                            const {title} = column;

                            const styles = getColumnHeaderWidthStyles(column);

                            const filterInfo = getTableColumnFilterInfo(column.dataIndex);

                            return (
                                <div
                                    className={cn('table-report__table__column-title')}
                                    style={{
                                        fontSize: headerFontSize,
                                    }}
                                >
                                    <div style={{...styles, flexGrow: 1}}>
                                        <NonbreakingSpaces>
                                            {title}
                                        </NonbreakingSpaces>
                                    </div>
                                    {(!disableActions && !enabledMenu) && (
                                        <Badge
                                            count={filterInfo.rulesCount}
                                            size="small"
                                        >
                                            <TableColumnFilter
                                                templateCode={templateCode}
                                                columnName={column.dataIndex}
                                                getTableColumnFilterData={createTableColumnFilterDataGetter(
                                                    column.dataIndex,
                                                )}
                                                setTableColumnFilterData={createTableColumnFilterDataSetter(
                                                    column.dataIndex,
                                                )}
                                                isApplied={filterInfo.isApplied}
                                                hasRolePermission={hasRolePermission}
                                                selectColumnFilterEntriesByFieldType={
                                                    selectTableReportFiltersByFieldType
                                                }
                                                tableAttributes={tableReportAttributesForFilters}
                                            />
                                        </Badge>
                                    )}
                                </div>
                            );
                        })()}
                        sortDirections={['ascend', 'descend', 'ascend']}
                        sorter={(() => {
                            const columnSortInfo = createSortDataGetter(column.dataIndex)();
                            if (columnSortInfo.order && columnSortInfo.multiple) {
                                return {
                                    compare: () => 0,
                                    multiple: columnSortInfo.multiple,
                                };
                            }
                            return undefined;
                        })()}
                        sortOrder={(() => {
                            const columnSortInfo = createSortDataGetter(column.dataIndex)();
                            return columnSortInfo.order ? columnSortInfo.order : null;
                        })()}
                        dataIndex={column.dataIndex}
                        key={column.key || column.dataIndex}
                        filterIcon={!isCustomColumn(column.dataIndex)
                          && column.dataIndex
                          && (
                              <span className="anticon anticon-filter">
                                  <MoreIcon
                                      height={14}
                                      width={14}
                                      fill="currentColor"
                                  />
                              </span>
                          )}
                        filterDropdown={(!isCustomColumn(column.dataIndex)
                          && column.dataIndex
                          && !disableActions)
                          && (
                              () => (
                                  <TableColumnMenu
                                      setSortData={createSortDataSetter(column.dataIndex)}
                                      getSortData={createSortDataGetter(column.dataIndex)}
                                      hideOptions={
                                          column.dataIndex.includes(TABLE_REPORT_URL_FIELD_NAME)
                                        && !linkDtoToColumnMapping?.[column.dataIndex]
                                      }
                                      handleHideColumnClick={() => hideColumn(column.dataIndex)}
                                  />
                              )
                          )}
                        render={(val, record, rowIndex) => {
                            const formatSettingsApplicationResult = applyFormatSettingsToRow({
                                dataIndex: column.dataIndex,
                                record,
                                rowFormats,
                                reportGroups,
                            });

                            return {
                                props: {
                                    className: cn({
                                        'table-report-string-column': isColumnTypeOfString(
                                            tableReportAttributes,
                                            column,
                                        ),
                                    }),
                                    style: {
                                        background: formatSettingsApplicationResult.background,
                                        ...getColumnWidthStyles(
                                            tableReportAttributes,
                                            column,
                                        ),
                                    },
                                },
                                children: (
                                    <div
                                        className="table-report__column"
                                    >
                                        <ReportColumnContent
                                            {...{
                                                record,
                                                value: val,
                                                columnIndex,
                                                rowIndex,
                                                formatSettingsApplicationResult,
                                                column,
                                                tableFontSize,
                                                pushWithBreadcrumbs,
                                                drillDowns,
                                                setDDModalEntries,
                                                setIsDDModalVisible,
                                                docId,
                                            }}
                                        />
                                    </div>
                                ),
                            };
                        }}
                    />
                </>
            ))}
        </>
    );
};

export const renderColumns = ({
    headerStructure, columns, columnProps, disableActions = false,
}: ITableReportHeaderProps) => {
    const {
        enabledRowAttachments,
        enabledRowComments,
        enabledRowRequests,
        enabledDrillUpBtn,
        loadTableReportData,
        docId,
        enabledPinHeaderButton,
        handlePinButtonClick,
        isTableHeaderPinned,
        enabledSelectAllRowsBtn,
        hasRolePermission,
    } = columnProps;

    const userCanDrillUp = hasRolePermission(EUserRoleFunction.VIEW_DRILL_UP) && enabledDrillUpBtn;

    const rowDrillUpComponent = (!disableActions && userCanDrillUp) && (
        <Column
            width={0}
            render={(val, record) => (
                <TableRowDrillUp
                    rowRecord={record as {[key: string]: any}}
                    headerStructure={headerStructure}
                    columns={columns}
                    columnProps={columnProps}
                    docId={docId}
                />
            )}
        />
    );

    const rowAttachmentsComponent = (!disableActions
    && (enabledRowAttachments || enabledRowComments || enabledRowRequests)) && (
        <Column
            width={80}
            render={(val, record) => (
                <TableRowAttachements
                    loadReportPageData={loadTableReportData}
                    rowRecord={record}
                    docId={docId}
                />
            )}
        />
    );

    const pinHeaderButton = (enabledPinHeaderButton && !disableActions) && (
        <Column
            fixed="left"
            width={0}
            title={(() => (
                <Button
                    onClick={handlePinButtonClick}
                    type="link"
                    className={cn('pin-button', {'pin-button_selected-column': enabledSelectAllRowsBtn})}
                >
                    <Tooltip
                        placement="rightTop"
                        trigger="hover"
                        title={isTableHeaderPinned ? 'Открепить шапку' : 'Закрепить шапку'}
                        getPopupContainer={getPopupContainer()}
                    >
                        {isTableHeaderPinned ? (
                            <PushpinFilled
                                rotate={-90}
                                style={{fontSize: '140%'}}
                            />
                        ) : (
                            <PushpinOutlined
                                rotate={-45}
                                style={{fontSize: '140%'}}
                            />
                        )}
                    </Tooltip>
                </Button>
            ))()}
        />
    );

    if (headerStructure?.length) {
        return (
            <>
                {pinHeaderButton}
                {rowDrillUpComponent}
                {rowAttachmentsComponent}
                {renderMultiRowHeader({
                    headerStructure, columns, columnProps, disableActions,
                })}
            </>
        );
    }

    return (
        <>
            {pinHeaderButton}
            {rowDrillUpComponent}
            {rowAttachmentsComponent}
            {renderSingleRowHeader({columns, columnProps, disableActions})}
        </>
    );
};
